import { makeStyles } from '@material-ui/styles'
import { listTablePrefs } from 'app/constants'
import { RootState } from 'app/store'
import { ArrayElement } from 'core/actions/Action'
import DeleteDialog from 'core/components/DeleteDialog'
import ServerSidePagination from 'core/components/server-side-pagination/ServerSidePagination'
import ServerSideSearch, {
  SearchFieldOption,
  ServerSideSearchParams,
} from 'core/components/server-side-pagination/ServerSideSearch'
import { FilterConfig } from 'core/components/server-side-pagination/ServerSideFilters'
import { SortFieldOption } from 'core/components/server-side-pagination/ServerSideSort'
import ListContainer from 'core/containers/ListContainer'
import Badge from 'core/elements/badge'
import { GridViewColumn } from 'core/elements/grid/Grid'
import Text from 'core/elements/Text'
import { createUsePrefParamsHook } from 'core/hooks/useParams'
import { SessionState, sessionStoreKey } from 'core/session/sessionReducers'
import Theme from 'core/themes/model'
import { routeManager } from 'core/utils/routes'
import DataKeys from 'k8s/DataKeys'
import { pick, prop } from 'ramda'
import { useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { formatTimestampToLocal } from '../../vms/helpers'
import { deletePort } from '../actions'
import { portsSelector } from '../selectors'
import CreatePortModal from './CreatePortModal'
import EditPortModal from './EditPortModal'
import { sortIpAddress } from './helper'
import usePortsPagination from './usePortsPagination'

type ModelDataKey = DataKeys.OpenstackPorts
type SelectorModel = ArrayElement<ReturnType<typeof portsSelector>>

const defaultParams = {
  orderBy: 'created_at',
  orderDirection: 'desc',
}

const useStyles = makeStyles<Theme>((theme) => ({
  portsPage: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  addressPairs: {
    display: 'grid',
    gap: 8,
  },
  addressPair: {
    whiteSpace: 'nowrap',
  },
  nameCell: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
  },
}))

// Search field options for ports
const PORTS_SEARCH_FIELDS: SearchFieldOption[] = [
  { value: 'name', label: 'Name' },
  { value: 'uuid', label: 'UUID' },
  { value: 'mac_address', label: 'MAC Address' },
  { value: 'ip_address', label: 'IP Address' },
]

// Filter configurations for ports
const PORTS_FILTER_CONFIGS: FilterConfig[] = [
  {
    key: 'admin_state',
    label: 'Admin State',
    options: [
      { value: 'all', label: 'All' },
      { value: 'true', label: 'Up' },
      { value: 'false', label: 'Down' },
    ],
    defaultValue: 'all',
  },
  {
    key: 'status',
    label: 'Status',
    options: [
      { value: 'all', label: 'All' },
      { value: 'ACTIVE', label: 'Active' },
      { value: 'ERROR', label: 'Error' },
      { value: 'DOWN', label: 'Down' },
    ],
    defaultValue: 'all',
  },
  {
    key: 'port_security_enabled',
    label: 'Port Security',
    options: [
      { value: 'all', label: 'All' },
      { value: 'true', label: 'Enabled' },
      { value: 'false', label: 'Disabled' },
    ],
    defaultValue: 'all',
  },
]

// Sort field options for ports
const PORTS_SORT_OPTIONS: SortFieldOption[] = [
  { value: 'created_at', label: 'Created' },
  { value: 'updated_at', label: 'Updated' },
  { value: 'ip_address', label: 'IP Address' },
  { value: 'name', label: 'Name' },
  { value: 'admin_state', label: 'Admin State' },
  { value: 'status', label: 'Status' },
  { value: 'port_security_enabled', label: 'Port Security' },
]

const AddressPairsCell = ({ value }) => {
  const classes = useStyles()
  return (
    <div className={classes.addressPairs}>
      {value?.map((pair) => (
        <div className={classes.addressPair}>
          <Text variant="body2">IP Address: {pair?.ip_address}</Text>
          <Text variant="body2">MAC Address: {pair?.mac_address}</Text>
        </div>
      ))}
    </div>
  )
}

function search(value: string | Array<string>, searchStr: string) {
  if (typeof value === 'string') {
    return value.toLowerCase().includes(searchStr.toLowerCase())
  }

  if (Array.isArray(value)) {
    const lws = searchStr.toLowerCase()
    return value.some((s) => s.toLowerCase().includes(lws))
  }
  // maybe handle nested array of objs????????????

  return false
}

type PossibleCellTypes = boolean | string | Array<{ [key: string]: string }>
function makeSearchFn<T extends PossibleCellTypes>(
  formatCellValue: (cellValue: T) => string | Array<string>,
) {
  return function searchFn(cellValue: T, searchTerm: string) {
    return search(formatCellValue(cellValue), searchTerm)
  }
}

const portColumns = ({ orderDirection, classes }): GridViewColumn<SelectorModel>[] => {
  return [
    {
      key: 'name',
      label: 'Name',
      width: 'medium',
      render: (value, item) => {
        return (
          <div className={classes.nameCell}>
            <Text variant="body2">{value}</Text>
            {item?.device_owner === 'network:distributed' && (
              <Badge text="System Port" variant="unknown" bold />
            )}
          </div>
        )
      },
    },
    {
      key: 'id',
      label: 'UUID',
      display: false,
    },
    {
      key: 'fixed_ips',
      label: 'Private IPs',
      render: (value) => value.map((ip) => ip?.ip_address).join(', '),
      sortFn: (a: { [x: string]: string }[], b: { [x: string]: string }[]) =>
        sortIpAddress(
          a?.map((ip) => ip?.ip_address) || [],
          b?.map((ip) => ip?.ip_address) || [],
          orderDirection,
        ),
    },
    {
      key: 'mac_address',
      label: 'MAC Address',
    },
    {
      key: 'binding:vif_type',
      label: 'VIF Type',
    },
    {
      key: 'device_owner',
      label: 'Attachment Type',
    },
    // {
    //   key: 'device',
    //   label: 'Attached Device'
    // },
    {
      key: 'status',
      label: 'Status',
    },
    {
      key: 'port_security_enabled',
      label: 'Port Security',
      render: (value) => (value ? 'Enabled' : 'Disabled'),
    },
    {
      key: 'securityGroups',
      label: 'Security Groups',
      render: (value) => value?.map((group) => group?.name || group?.id)?.join(', '),
    },
    {
      key: 'allowed_address_pairs',
      label: 'Allowed Address Pairs',
      CellComponent: AddressPairsCell,
    },
    {
      key: 'admin_state_up',
      label: 'Admin State',
      render: (value) => (value ? 'Up' : 'Down'),
    },
    {
      key: 'dns_name',
      label: 'DNS Name',
    },
    {
      key: 'created_at',
      label: 'Created',
      render: (time: string) => formatTimestampToLocal(time),
      display: false,
    },
  ]
}

const searchFunctions = [
  {
    property: 'mac_address',
    searchFn: makeSearchFn<string>((cellValue) => cellValue),
  },
  {
    property: 'securityGroups',
    searchFn: makeSearchFn<Array<{ name: string; id: string }>>((cellValue) =>
      cellValue?.map((group) => group?.name || group?.id)?.join(','),
    ),
  },
  {
    property: 'allowed_address_pairs',
    searchFn: makeSearchFn<Array<{ ip_address: string; mac_address: string }>>((cellValue) =>
      cellValue.map((cell) => `${cell.ip_address}__${cell.mac_address}`).join(','),
    ),
  },
  {
    property: 'port_security_enabled',
    searchFn: makeSearchFn<boolean>((cellValue) => (cellValue ? 'enabled' : 'disabled')),
  },
  {
    property: 'fixed_ips',
    // makeSearchFn<Array<{ [key: string]}>>()
    searchFn: (value, searchString) => {
      const lowerSearch = searchString?.toLowerCase()
      return value?.some(
        (ipObj) => ipObj?.ip_address && ipObj.ip_address.toLowerCase().includes(lowerSearch),
      )
    },
  },
]

const usePrefParams = createUsePrefParamsHook('Ports', listTablePrefs)

export default function PortsPage({ network, loading, allowCrud = true }) {
  const classes = useStyles()
  const session = useSelector<RootState, SessionState>(prop(sessionStoreKey))
  const { features } = session
  const isVmware = features?.experimental?.pmov2_du_type === 'vmware'
  const { params, getParamsUpdater } = usePrefParams(defaultParams)

  // Server-side pagination with search/filter/sort for ports
  const {
    paginatedData: networkPorts,
    loading: loadingPorts,
    handleRefresh: reloadPorts,
    handleSearch,
    handleClearSearch,
    searchParams,
    paginationProps,
  } = usePortsPagination({
    networkId: network.id,
  })

  // Handle search/filter/sort from the search component
  const onSearch = useCallback(
    (params: ServerSideSearchParams) => {
      handleSearch({
        searchField: params.searchField,
        searchValue: params.searchValue,
        filters: params.filters,
        sortKey: params.sortKey,
        sortDir: params.sortDir,
      })
    },
    [handleSearch],
  )

  // Handle clear search
  const onClear = useCallback(() => {
    handleClearSearch()
  }, [handleClearSearch])

  const addRoute = isVmware
    ? routeManager.routes.openstack.createVmwPort
    : routeManager.routes.openstack.createNetworkPort

  const columns = useMemo(() => portColumns({ orderDirection: params.orderDirection, classes }), [
    params.orderDirection,
  ])

  const DeletePortDialog = useCallback(
    (props) => (
      <DeleteDialog
        {...props}
        deleteAction={deletePort}
        title="Delete Port(s)"
        resourceLabel="Ports"
      />
    ),
    [],
  )

  return (
    <article className={classes.portsPage}>
      <CreatePortModal
        addRoute={addRoute}
        reloadPorts={reloadPorts}
        isSimpleNetwork={network?.isSimpleNetwork}
      />
      {/* Server-side search, filters, and sort */}
      <ServerSideSearch
        searchFields={PORTS_SEARCH_FIELDS}
        filterConfigs={PORTS_FILTER_CONFIGS}
        sortOptions={PORTS_SORT_OPTIONS}
        onSearch={onSearch}
        onClear={onClear}
        isLoading={loadingPorts}
        initialParams={{
          searchField: searchParams.searchField,
          searchValue: searchParams.searchValue,
          filters: searchParams.filters,
          sortKey: searchParams.sortKey,
          sortDir: searchParams.sortDir,
        }}
        defaultSortKey="created_at"
        defaultSortDir="desc"
      />
      <ListContainer<ModelDataKey, SelectorModel>
        showBreadcrumbs={false}
        dataKey={DataKeys.OpenstackPorts}
        searchTargets={[]}
        uniqueIdentifier="id"
        loading={loading || loadingPorts}
        hideHeaderPortals
        onRefresh={reloadPorts}
        data={networkPorts}
        columns={columns}
        editCond={(selectedItems) =>
          selectedItems?.length === 1 &&
          selectedItems?.every(
            (selectedItem) => selectedItem?.device_owner !== 'network:distributed',
          )
        }
        editDisabledInfo="You can edit a single port at a time, and it must not be a system-managed (Distributed Network) port."
        deleteCond={(selectedItems) =>
          selectedItems?.every(
            (selectedItem) => selectedItem?.device_owner !== 'network:distributed',
          )
        }
        deleteDisabledInfo="One or more selected ports are system-managed (Distributed Network) and cannot be deleted."
        DeleteDialogComponent={DeletePortDialog}
        addUrl={allowCrud ? addRoute.path({ id: network?.id }) : undefined}
        addText={allowCrud ? 'Create Port' : undefined}
        EditDialogComponent={allowCrud ? EditPortModal : undefined}
        deleteAction={deletePort}
        getParamsUpdater={getParamsUpdater}
        multiSelection
        // Server-side pagination
        disableSearch
        disableSorting
        disablePagination
        CustomPaginationComponent={() => <ServerSidePagination {...paginationProps} />}
        {...pick(listTablePrefs, params)}
      />
    </article>
  )
}
